import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getRecommends, RecommendedTimeline } from '../api/recommendApi';
import { addFavorite, removeFavorite, getMyFavorites, FavoriteTopicArea } from '../api/favoriteApi';
import { getTopicAreaCategories, getTopicAreaTags } from '../api/topicAreaApi';
import { buildCategoryHierarchy } from '../utils/categoryUtils';
import axiosInstance from '../api/axiosInstance';
import { Content, Category, Tag } from '../types';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'react-toastify';
import {
  clearContentReturnState,
  ContentReturnNavigationState,
  ContentReturnState,
  getContentReturnState,
  markCurrentHistoryForContentReturn,
  saveContentReturnState
} from '../utils/contentReturnState';
import './RecommendedTimelinePage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const getCardThumbnailSrc = (content: Content): string | null => {
  const primaryAttachment = content.attachments?.[0];
  const isImage = primaryAttachment?.file_type === '이미지' ||
    primaryAttachment?.file_name?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

  if (primaryAttachment && isImage) {
    const fileUrl = primaryAttachment.thumb_middle || primaryAttachment.thumb_small || primaryAttachment.file_url;
    return /^https?:\/\//i.test(fileUrl) ? fileUrl : `${API_BASE_URL}${fileUrl}`;
  }

  if (content.thumbnail) {
    return /^https?:\/\//i.test(content.thumbnail) ? content.thumbnail : `${API_BASE_URL}${content.thumbnail}`;
  }

  return null;
};

const RecommendedTimelinePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [recommends, setRecommends] = useState<RecommendedTimeline[]>([]);
  const [selectedRecommend, setSelectedRecommend] = useState<RecommendedTimeline | null>(null);
  const [contents, setContents] = useState<Content[]>([]);
  const [favorites, setFavorites] = useState<FavoriteTopicArea[]>([]);

  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);

  const [loading, setLoading] = useState(true);
  const [contentsLoading, setContentsLoading] = useState(false);
  const [activeYear, setActiveYear] = useState<string>('');
  const [returnState] = useState<ContentReturnState | null>(() => {
    const navigationState = location.state as ContentReturnNavigationState | null;
    return navigationState?.restoreContentScroll
      ? getContentReturnState(location.pathname)
      : null;
  });
  const hasHandledReturnScroll = useRef(false);

  useEffect(() => {
    const navigationState = location.state as ContentReturnNavigationState | null;
    if (navigationState?.restoreContentScroll) return;

    window.scrollTo({ top: 0, behavior: 'auto' });
    hasHandledReturnScroll.current = true;
  }, [location.key, location.state]);

  // contents 로드 시 최초 연도로 activeYear 초기화
  useEffect(() => {
    if (contents.length > 0) {
      const firstYear = new Date(contents[0].timestamp).getFullYear().toString();
      setActiveYear(firstYear);
    } else {
      setActiveYear('');
    }
  }, [contents]);

  // 스크롤 시 뷰포트 내 활성 연도 실시간 감지
  useEffect(() => {
    if (contents.length === 0) return;

    const handleScroll = () => {
      const nodes = document.querySelectorAll('.timeline-vertical-node');
      let currentYear = '';
      const triggerPoint = 200; // 화면 상단 기준선 (px)

      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i] as HTMLElement;
        const rect = node.getBoundingClientRect();
        if (rect.top <= triggerPoint) {
          currentYear = node.getAttribute('data-year') || '';
        } else {
          if (i === 0) {
            currentYear = node.getAttribute('data-year') || '';
          }
          break;
        }
      }

      if (currentYear) {
        setActiveYear(prev => {
          if (prev !== currentYear) {
            return currentYear;
          }
          return prev;
        });
      }
    };

    window.addEventListener('scroll', handleScroll);
    // 마운트 직후와 레이아웃 완료 후 즉시 갱신을 위해 약간의 지연 처리
    const timer = setTimeout(handleScroll, 100);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, [contents]);

  // 1. 초기 추천 데이터 및 카테고리/관심목록 로드
  useEffect(() => {
    const initData = async () => {
      setLoading(true);
      try {
        const [recommendsData, allCatsData] = await Promise.all([
          getRecommends(),
          buildCategoryHierarchy()
        ]);

        setRecommends(recommendsData);
        setAllCategories(allCatsData);

        // 로그인 상태이면 관심 목록 조회
        if (user) {
          const favs = await getMyFavorites();
          setFavorites(favs);
        }

        // 최신 추천 1건 기본 선택
        if (recommendsData.length > 0) {
          const restoredRecommend = recommendsData.find(item => item.id === returnState?.selectedRecommendId);
          setSelectedRecommend(restoredRecommend || recommendsData[0]);
        }
      } catch (err) {
        console.error('추천 타임라인 로드 실패:', err);
        toast.error('추천 목록을 불러오는 데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };
    initData();
  }, [returnState, user]);

  useEffect(() => {
    if (hasHandledReturnScroll.current) return;
    if (loading || contentsLoading || contents.length === 0) return;

    if (returnState?.scrollY === undefined) return;

    const timer = window.setTimeout(() => {
      window.scrollTo({ top: returnState.scrollY, behavior: 'auto' });
      clearContentReturnState(location.pathname);
      hasHandledReturnScroll.current = true;
    }, 0);

    return () => window.clearTimeout(timer);
  }, [contents, contentsLoading, loading, location.pathname, returnState]);

  const handleContentClick = (contentId: number) => {
    saveContentReturnState({
      returnTo: location.pathname,
      scrollY: window.scrollY,
      selectedRecommendId: selectedRecommend?.id
    });
    markCurrentHistoryForContentReturn();
    navigate(`/content/${contentId}`, { state: { returnTo: location.pathname } });
  };

  // 2. 선택된 추천 주제영역 정보 및 컨텐츠 로드
  useEffect(() => {
    if (!selectedRecommend) return;

    const loadTopicDetails = async () => {
      setContentsLoading(true);
      try {
        const topicId = selectedRecommend.topic_area_id;

        // 상세 카테고리, 태그, 컨텐츠 로드
        const [catsData, tagsData, contentsRes] = await Promise.all([
          getTopicAreaCategories(topicId),
          getTopicAreaTags(topicId),
          axiosInstance.get('/api/contents', { params: { topic_area_id: topicId } })
        ]);

        setCategories(catsData);
        setTags(tagsData);
        setContents(contentsRes.data);
      } catch (err) {
        console.error('주제영역 데이터 로드 실패:', err);
        toast.error('타임라인 사건 목록을 불러오는 데 실패했습니다.');
      } finally {
        setContentsLoading(false);
      }
    };

    loadTopicDetails();
  }, [selectedRecommend]);

  // 3. 관심 상태 여부 확인
  const isFavorite = selectedRecommend
    ? favorites.some(fav => fav.topic_area_id === selectedRecommend.topic_area_id)
    : false;

  // 4. 관심 토글
  const handleFavoriteToggle = async () => {
    if (!user) {
      toast.warn('로그인 후 이용할 수 있습니다.');
      navigate('/login');
      return;
    }
    if (!selectedRecommend) return;

    const topicId = selectedRecommend.topic_area_id;
    try {
      if (isFavorite) {
        await removeFavorite(topicId);
        toast.success('관심 주제영역에서 해제되었습니다.');
      } else {
        await addFavorite(topicId);
        toast.success('관심 주제영역으로 등록되었습니다!');
      }

      // 상태 갱신
      const favs = await getMyFavorites();
      setFavorites(favs);
    } catch (err) {
      console.error('관심 등록/해제 실패:', err);
      toast.error('오류가 발생했습니다.');
    }
  };

  const handleRecommendChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (!val) {
      setSelectedRecommend(null);
      return;
    }
    const found = recommends.find(r => r.id === Number(val));
    if (found) {
      hasHandledReturnScroll.current = true;
      window.scrollTo({ top: 0, behavior: 'auto' });
      setSelectedRecommend(found);
    }
  };

  if (loading) {
    return <div className="recommend-page-loading">추천 타임라인을 구성 중입니다...</div>;
  }

  return (
    <div className="recommend-page-container">
      <div className="recommend-page-header">
        <div>
          <h2>⭐ 추천 타임라인</h2>
          <p className="recommend-page-subtitle">엄선한 고품격 역사 주제와 시간선입니다.</p>
        </div>

        {recommends.length > 0 && (
          <div className="recommend-selector-row">
            <select
              value={selectedRecommend?.id || ''}
              onChange={handleRecommendChange}
              className="recommend-select"
            >
              {recommends.map(r => (
                <option key={r.id} value={r.id}>
                  {r.title}
                </option>
              ))}
            </select>

            <button
              onClick={handleFavoriteToggle}
              className={`favorite-toggle-btn ${isFavorite ? 'active' : ''}`}
            >
              {isFavorite ? '⭐ 관심 해제' : '☆ 관심 주제영역 저장'}
            </button>
          </div>
        )}
      </div>

      {recommends.length === 0 ? (
        <div className="recommend-empty">
          <div className="empty-icon">⭐</div>
          <h3>추천된 타임라인이 아직 없습니다.</h3>
          <p>주제영역 상세페이지에서 운영자에게 추천 신청을 보낼 수 있습니다.</p>
        </div>
      ) : selectedRecommend && (
        <div className="recommend-body-layout">
          {/* 주제 설명 카드 */}
          <div className="recommend-topic-summary-card">
            <h3>{selectedRecommend.title}</h3>
            <p className="summary-desc">{selectedRecommend.summary}</p>

            <div className="summary-meta-grid">
              {categories.length > 0 && (
                <div className="summary-meta-item">
                  <span className="meta-label">📂 카테고리</span>
                  <div className="meta-chips">
                    {categories.map(c => (
                      <span key={c.id} className="meta-chip category">
                        {c.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {tags.length > 0 && (
                <div className="summary-meta-item">
                  <span className="meta-label">🏷️ 태그</span>
                  <div className="meta-chips">
                    {tags.map(t => (
                      <span key={t.id} className="meta-chip tag">
                        #{t.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="timeline-link-banner" onClick={() => navigate(`/timeline/${selectedRecommend.topic_area_id}`)}>
              📅 이 주제영역의 다이나믹 인터랙티브 가로 시간선 보러가기 ➔
            </div>
          </div>

          {/* 타임라인 컨텐츠 목록 */}
          <div className="recommend-timeline-stream">
            {contentsLoading ? (
              <div className="stream-loading">사건들을 시간선상에 배치 중입니다...</div>
            ) : contents.length === 0 ? (
              <div className="stream-empty">이 추천 타임라인에 등록된 사건 기록이 없습니다.</div>
            ) : (
              <>
                {activeYear && (
                  <div key={activeYear} className="timeline-sticky-year">
                    {activeYear}
                  </div>
                )}
                <div className="timeline-vertical-list">
                  <div className="timeline-line-axis" />
                  {contents.map((item, index) => {
                    const date = new Date(item.timestamp);
                    const year = date.getFullYear();
                    const prevDate = index > 0 ? new Date(contents[index - 1].timestamp) : null;
                    const prevYear = prevDate ? prevDate.getFullYear() : null;
                    const isNewYear = prevYear === null || year !== prevYear;

                    return (
                      <React.Fragment key={item.id}>
                        {isNewYear && (
                          <div className="timeline-year-divider">
                            <span>{year}년</span>
                          </div>
                        )}
                        <div
                          className={`timeline-vertical-node ${index % 2 === 0 ? 'left' : 'right'}`}
                          data-year={year}
                          onClick={() => handleContentClick(item.id)}
                        >
                          <div className="timeline-node-dot" />
                          <div className="timeline-node-card">
                            <div className="node-date">
                              📅 {date.getFullYear()}년 {date.getMonth() + 1}월 {date.getDate()}일
                            </div>
                            <h4 className="node-title">{item.title}</h4>
                            {(() => {
                              const thumbnailSrc = getCardThumbnailSrc(item);
                              return thumbnailSrc ? (
                                <div className="node-image-wrapper">
                                  <img src={thumbnailSrc} alt={item.title} className="node-card-image" />
                                </div>
                              ) : null;
                            })()}
                            <p className="node-desc">{item.description}</p>

                            {item.tags && item.tags.length > 0 && (
                              <div className="node-tags">
                                {item.tags.map(t => (
                                  <span key={t.id} className="node-tag-item">#{t.name}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RecommendedTimelinePage;
